import { Box, Checkbox, Text, useToast } from '@chakra-ui/react'
import { observer } from 'mobx-react-lite'
import { getSnapshot, isAlive } from 'mobx-state-tree'
import { useEffect, useRef, useState } from 'react'

import { db } from '../../data/dexie/Database'
import { useMst } from '../../data/stores/rootStore'
import type { MatchSettingsRecoveryContext, MatchSettingsRecoveryPreview } from '../../helpers/matchSettingsRecovery'
import {
  applyMatchSettingsRecovery,
  fetchRecoveryMatchFormat,
  matchSettingsRecoveryBlock,
  prepareMatchSettingsRecovery,
  recoveryDeliveryInProgress,
  recoveryFormatName,
} from '../../helpers/matchSettingsRecovery'
import { canRestorePrematchOvers, hasPositiveOvers, isLimitedOversFormat } from '../../helpers/matchSettingsValidation'
import { isSampleMatch } from '../../helpers/sampleMatch'
import type { MatchSettingsProps } from '../../types/props'
import { Button } from '../Buttons/Button'

export const MatchSettingsRecovery = observer(({ game, mode }: MatchSettingsProps) => {
  const root = useMst()
  const toast = useToast()
  const [preview, setPreview] = useState<MatchSettingsRecoveryPreview | null>(null)
  const [automaticFreeHits, setAutomaticFreeHits] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const running = useRef(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const context: MatchSettingsRecoveryContext = {
    state: () => {
      const activeInning = game.getActiveInning()
      const inningBalls = activeInning && root.balls.results.get(activeInning.id)
      return {
        matchId: game.id,
        matchFormatId: game.matchFormatId,
        maxOvers: game.matchConfigs.maxOvers,
        canRestoreOvers: canRestorePrematchOvers(
          game.matchStatusId,
          game.matchTeams.reduce((count, team) => count + team.innings.length, 0)
        ),
        mode,
        socketMatchId: root.socketStore.matchId,
        connected: root.socketStore.connected(),
        primary: root.socketStore.primary,
        assignedRole: root.socketStore.assignedRole,
        manualScoring: root.appSettings.manualScoring.active,
        timeMachine: !!root.appSettings.timeMachine.activated,
        ballsLoaded: !activeInning || !!inningBalls,
        deliveryInProgress: !!inningBalls?.balls.some(recoveryDeliveryInProgress),
        available: mounted.current && isAlive(game),
      }
    },
    revision: () => [getSnapshot(game), getSnapshot(root.balls.results), getSnapshot(root.timelineEvents.results)],
    hasPendingUpdates: () =>
      db.transaction('r', db.matches, db.timeline, db.s3p, async () => {
        const [match, timelineCount, s3pCount] = await Promise.all([
          db.matches.get(game.id),
          db.timeline.where('[matchId+syncRequired]').equals([game.id, 1]).count(),
          db.s3p.where('[matchId+syncRequired]').equals([game.id, 1]).count(),
        ])
        return !match || match.syncRequired !== 0 || timelineCount > 0 || s3pCount > 0
      }),
    readFormat: fetchRecoveryMatchFormat,
    apply: (format, automatic, maxOvers) => game.restoreMatchSettings(format, automatic, maxOvers),
  }

  const run = async (apply: boolean) => {
    if (running.current) return
    running.current = true
    setBusy(true)
    setError(null)
    try {
      if (apply && preview) {
        await applyMatchSettingsRecovery(context, preview, automaticFreeHits)
        toast({
          title: 'Match settings restored.',
          description: 'Wait for sync, then review and confirm the match settings before scoring.',
          status: 'success',
          duration: 10000,
          isClosable: true,
        })
        setPreview(null)
      } else {
        const result = await prepareMatchSettingsRecovery(context)
        if (mounted.current) {
          setPreview(result)
          setAutomaticFreeHits(false)
        }
      }
    } catch (reason) {
      if (mounted.current) {
        setPreview(null)
        setError(reason instanceof Error ? reason.message : 'Unable to restore the match format. Please retry.')
      }
    } finally {
      running.current = false
      if (mounted.current) setBusy(false)
    }
  }

  const unknownFormat = game.matchFormatId === 0 || game.matchFormatId === null
  const missingOvers = isLimitedOversFormat(game.matchFormatId) && !hasPositiveOvers(game.matchConfigs.maxOvers)
  if (mode !== 'advanced' || isSampleMatch(game.id) || (!unknownFormat && !missingOvers)) {
    return null
  }
  const blocked = matchSettingsRecoveryBlock(context.state())

  return (
    <Box marginY="14px" padding="14px" borderWidth="1px" borderRadius="7px" data-testid="matchSettingsRecovery">
      <Text fontWeight="bold">{unknownFormat ? 'Match format is missing' : 'Overs limit is missing'}</Text>
      <Text marginY="7px">
        Check the saved match format and overs before scoring. Missing overs can be restored before innings exist.
      </Text>
      {blocked && <Text role="status">{blocked}</Text>}
      {error && <Text role="alert">{error}</Text>}
      {preview ? (
        <>
          <Text marginY="7px">
            Restore {recoveryFormatName(preview.format)}.
            {preview.maxOvers !== undefined
              ? ` Set missing Overs per innings to ${preview.maxOvers}.`
              : ' Keep the current Overs per innings.'}
          </Text>
          <Text marginY="7px">Review the free-hit choice before scoring.</Text>
          {isLimitedOversFormat(preview.format) && (
            <Checkbox
              marginBottom="14px"
              isChecked={automaticFreeHits}
              isDisabled={busy || !!blocked}
              onChange={event => setAutomaticFreeHits(event.target.checked)}
            >
              Enable automatic free hits after no balls
            </Checkbox>
          )}
          <Box>
            <Button isDisabled={!!blocked} isLoading={busy} onClick={() => run(true)}>
              Restore match settings
            </Button>
            <Button marginLeft="7px" isDisabled={busy} onClick={() => setPreview(null)}>
              Cancel
            </Button>
          </Box>
        </>
      ) : (
        <Button marginTop="7px" isDisabled={!!blocked} isLoading={busy} onClick={() => run(false)}>
          Check match settings
        </Button>
      )}
    </Box>
  )
})
